import React, { useEffect, useRef, useState } from "react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { toast } from "sonner";
import { useRoundData } from "@/components/strategy/useRound";
import { useStrategy } from "@/context/StrategyContext";
import { Panel, PageHead, Loading, ErrorNote, KindBadge, Stat, DemoBadge, Tip } from "@/components/strategy/ui";
import { sapi, errText, gbp } from "@/lib/strategy";

const GROUPS = [
  { title: "Market (accounts ≠ branches ≠ users)", keys: ["eligible_accounts", "eligible_branches", "users_per_branch", "adoption_pct"] },
  { title: "Pricing & acquisition", keys: ["price", "acquisition_per_month", "independent_per_month", "independent_ramp_months", "churn_pct"] },
  { title: "Economics", keys: ["gross_margin_pct", "nichecom_share_pct", "ai_cost_monthly", "dev_cost_monthly", "target_mrr"] },
];

export default function RevenueModel() {
  const { round, roundId } = useStrategy();
  const { data, setData, loading, error, reload } = useRoundData("revenue");
  const [inputs, setInputs] = useState(null);
  const [scenario, setScenario] = useState("base");
  const timer = useRef(null);
  useEffect(() => { if (data && !inputs) setInputs(data.inputs); }, [data, inputs]);

  const change = (k, v) => {
    const next = { ...inputs, [k]: v };
    setInputs(next);
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try { const { data: d } = await sapi.put(`/rounds/${roundId}/revenue`, { inputs: next }); setData(d); } catch (e) { toast.error(errText(e)); }
    }, 450);
  };

  if (loading || !data || !inputs) return <Loading label="Building the model" />;
  if (error) return <ErrorNote error={error} retry={reload} />;
  const sc = data.scenarios[scenario];
  const s = sc.summary;
  const cc = data.channel_comparison;

  return (
    <div className="space-y-8">
      <PageHead eyebrow="Revenue modelling" title="Recurring revenue model" sub="How quickly can Growth build meaningful recurring revenue, and how can it reduce reliance on NicheCom over time? Every assumption is editable; the billable unit is the branch."
        actions={round?.demo && <DemoBadge />} />

      <div className="grid lg:grid-cols-[380px_1fr] gap-6 items-start">
        <div className="space-y-4" data-testid="revenue-inputs">
          {GROUPS.map((g) => (
            <Panel key={g.title} className="p-5">
              <p className="st-caps mb-3">{g.title}</p>
              <div className="space-y-3">
                {g.keys.map((k) => {
                  const m = data.meta[k];
                  return (
                    <label key={k} className="block">
                      <span className="flex items-center justify-between mb-1"><span className="text-xs font-semibold">{m.label}</span><KindBadge kind={m.kind} small /></span>
                      <div className="relative">
                        {m.unit === "£" && <span className="absolute left-3 top-1/2 -translate-y-1/2 st-faint text-sm">£</span>}
                        <input type="number" className={`st-input !py-2 text-sm ${m.unit === "£" ? "!pl-7" : ""}`} value={inputs[k]} onChange={(e) => change(k, e.target.value === "" ? 0 : Number(e.target.value))} data-testid={`rev-input-${k}`} />
                        {m.unit === "%" && <span className="absolute right-3 top-1/2 -translate-y-1/2 st-faint text-sm">%</span>}
                      </div>
                    </label>
                  );
                })}
              </div>
            </Panel>
          ))}
          <p className="st-faint text-xs leading-relaxed">{data.note}</p>
        </div>

        <div className="space-y-6">
          <div className="flex gap-2">
            {Object.entries(data.scenarios).map(([k, v]) => (
              <button key={k} onClick={() => setScenario(k)} className="st-btn !py-2 text-sm" data-testid={`rev-scenario-${k}`} style={scenario === k ? { background: "var(--st-lime-dim)", color: "var(--st-lime)", border: "1px solid rgba(200,247,74,.4)" } : { background: "rgba(243,239,230,.05)", color: "var(--st-muted)", border: "1px solid var(--st-line)" }}>{v.label}</button>
            ))}
            <span className="ml-auto st-faint text-xs self-center hidden sm:block">Scenario multipliers: adoption ×{sc.inputs.adoption_pct && (sc.inputs.adoption_pct / inputs.adoption_pct || 1).toFixed(1)} · acquisition ×{(sc.inputs.acquisition_per_month / (inputs.acquisition_per_month || 1)).toFixed(1)}</span>
          </div>
          <div className="grid grid-cols-2 xl:grid-cols-4 gap-3" data-testid="rev-summary">
            <Stat label="MRR · month 12" value={gbp(s.mrr_m12)} hint={`ARR ${gbp(s.arr_m12)}`} tone="var(--st-lime)" testid="rev-mrr-12" />
            <Stat label={`MRR · month ${inputs.months}`} value={gbp(s.mrr_end)} hint={`ARR ${gbp(s.arr_end)} · ${Math.round(s.branches_end)} branches`} testid="rev-mrr-end" />
            <Stat label="Break-even" value={s.breakeven_month ? `Month ${s.breakeven_month}` : "Not in horizon"} hint={`Contribution m12 ${gbp(s.contribution_m12)}`} tone={s.breakeven_month ? "var(--st-lime)" : "var(--st-amber)"} testid="rev-breakeven" />
            <Stat label={`Branches for ${gbp(inputs.target_mrr)} MRR`} value={s.customers_for_target ?? "—"} hint={s.target_month ? `Reached month ${s.target_month}` : "Not reached in horizon"} testid="rev-target" />
          </div>
          <Panel className="p-6 st-recharts" data-testid="rev-chart">
            <p className="st-caps mb-3">Monthly recurring revenue by channel · {sc.label}</p>
            <div style={{ height: 300 }}>
              <ResponsiveContainer>
                <AreaChart data={sc.series} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gNc" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#6FA8FF" stopOpacity={0.5} /><stop offset="100%" stopColor="#6FA8FF" stopOpacity={0.05} /></linearGradient>
                    <linearGradient id="gInd" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#C8F74A" stopOpacity={0.6} /><stop offset="100%" stopColor="#C8F74A" stopOpacity={0.05} /></linearGradient>
                  </defs>
                  <CartesianGrid vertical={false} />
                  <XAxis dataKey="month" tickFormatter={(m) => `M${m}`} />
                  <YAxis tickFormatter={(v) => `£${Math.round(v / 1000)}k`} />
                  <Tooltip content={<Tip fmt={(v) => gbp(v)} />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Area type="monotone" dataKey="mrr_nichecom" name="NicheCom-sourced MRR" stackId="1" stroke="#6FA8FF" fill="url(#gNc)" strokeWidth={2} />
                  <Area type="monotone" dataKey="mrr_independent" name="Independent MRR" stackId="1" stroke="#C8F74A" fill="url(#gInd)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Panel>
          <div className="grid md:grid-cols-2 gap-4">
            <Panel className="p-6" data-testid="rev-pl">
              <p className="st-caps mb-3">Economics · month 12 · {sc.label}</p>
              {[["Gross revenue (12 months)", gbp(s.gross_revenue_12m)], ["Gross profit · month 12", gbp(s.gross_profit_m12)], ["Operating contribution · month 12", gbp(s.contribution_m12)],
                [`Cumulative contribution · month ${inputs.months}`, gbp(s.cumulative_end)], ["Branches to break even", s.customers_for_breakeven ?? "—"], ["Users (not billed)", sc.series[11]?.users]].map(([l, v]) => (
                <div key={l} className="flex justify-between py-2 border-b text-sm" style={{ borderColor: "var(--st-line)" }}><span className="st-muted">{l}</span><b>{v}</b></div>
              ))}
            </Panel>
            <Panel className="p-6" data-testid="rev-channels">
              <p className="st-caps mb-1">NicheCom-sourced vs independent · base</p>
              <p className="st-faint text-xs mb-3">What happens if only one channel works.</p>
              {[["NicheCom only", cc.nichecom_only], ["Independent only", cc.independent_only], ["Combined", cc.combined]].map(([l, v]) => (
                <div key={l} className="flex justify-between py-2 border-b text-sm" style={{ borderColor: "var(--st-line)" }}><span className="st-muted">{l}</span><span><b>{gbp(v.mrr_end)}</b> MRR · {v.breakeven_month ? `BE m${v.breakeven_month}` : "no BE"}</span></div>
              ))}
              <p className="text-sm mt-3">Independent share of MRR by month {inputs.months}: <b style={{ color: "var(--st-lime)" }}>{s.independent_share_end_pct}%</b></p>
            </Panel>
          </div>
        </div>
      </div>
    </div>
  );
}
